import {
  CSVRow,
  CSVValidationResult,
  validateCSVRow,
} from '../../../validation';

export const validateCSVAsync = async (
  file: File,
  chunkSize = 100,
  onProgress?: (progress: number) => void,
): Promise<CSVValidationResult> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = async (event) => {
      try {
        const text = event.target?.result as string;
        const lines = text.split('\n').filter((line) => line.trim() !== '');

        if (lines.length < 2) {
          resolve({
            isValid: false,
            errors: [
              'O arquivo deve conter pelo menos um cabeçalho e uma linha de dados',
            ],
          });
          return;
        }

        const CSV_SEPARATOR = lines[0].includes(';') ? ';' : ',';

        const headerValidation = validateCSVHeader(lines[0], CSV_SEPARATOR);
        if (!headerValidation.isValid) {
          resolve({
            isValid: false,
            errors: headerValidation.errors,
          });
          return;
        }

        const dataLines = lines.slice(1);
        const errors: string[] = [];
        const data: CSVRow[] = [];

        const totalLines = dataLines.length;
        let processedLines = 0;

        const processChunk = async (startIndex: number): Promise<void> => {
          return new Promise((resolveChunk) => {
            setTimeout(() => {
              const endIndex = Math.min(startIndex + chunkSize, totalLines);

              for (let i = startIndex; i < endIndex; i++) {
                const line = dataLines[i];
                const lineNumber = i + 2; // starts on line 2 of csv
                const columns = line
                  .split(CSV_SEPARATOR)
                  .map((col) => col.trim());

                if (columns.length < 1) {
                  errors.push(
                    `Linha ${lineNumber}: Linha vazia ou sem colunas`,
                  );
                  continue;
                }

                try {
                  const row: CSVRow = {
                    cnpj_fundo: columns[0],
                    subclasse: columns[1] || undefined,
                    serie: columns[2] || undefined,
                    quantidade: columns[3] || undefined,
                  };

                  const rowValidation = validateCSVRow(row, lineNumber);
                  if (rowValidation.errors.length > 0) {
                    errors.push(...rowValidation.errors);
                  } else {
                    data.push(row);
                  }
                } catch (error) {
                  errors.push(`Linha ${lineNumber}: Erro ao processar dados`);
                }
              }

              processedLines = endIndex;
              onProgress?.(Math.round((processedLines / totalLines) * 100));

              resolveChunk();
            }, 0);
          });
        };

        for (let i = 0; i < totalLines; i += chunkSize) {
          await processChunk(i);
        }

        resolve({
          isValid: errors.length === 0,
          errors,
          data: errors.length === 0 ? data : undefined,
        });
      } catch (error) {
        reject({
          isValid: false,
          errors: ['Erro ao ler o arquivo CSV'],
        });
      }
    };

    reader.onerror = () => {
      reject({
        isValid: false,
        errors: ['Erro ao ler o arquivo CSV'],
      });
    };

    reader.readAsText(file);
  });
};

const validateCSVHeader = (
  headerLine: string,
  separator = ',',
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  const header = headerLine
    .split(separator)
    .map((col) => col.trim().toLowerCase());

  if (!header.includes('cnpj_fundo')) {
    errors.push('Coluna obrigatória ausente: cnpj_fundo');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};
